<template>
  <div
    class="app-layout-wrapper min-h-screen bg-[#f3f7ff] flex flex-col font-sans text-gray-900 w-full overflow-x-hidden">
    <!-- Fixed Top Nav Header -->
    <header class="app-header fixed top-0 left-0 right-0 z-40 w-full shadow-xs shrink-0">
      <AppTopNav :links="navLinks" :userData="userData" :orgLogo="orgLogo" :orgQrCode="orgQrCode"
        :notificationsCount="notificationsCount" @open-profile="navToProfile"
        @open-notifications="navToNotifications" />
    </header>

    <!-- Main Content Body (Natural scrolling with top & bottom clearance) -->
    <main class="app-main-content flex-1 flex flex-col w-full max-w-7xl mx-auto px-3 sm:px-6 box-border">
      <slot />
    </main>

    <!-- Bottom Nav for Mobile Screens -->
    <nav class="app-mobile-nav md:hidden fixed bottom-0 left-0 right-0 z-40">
      <AppBottomNav :links="navLinks" />
    </nav>

    <!-- Global Under Construction Dialog Modal -->
    <v-dialog v-model="underConstructionModal" max-width="400">
      <v-card class="rounded-3xl pa-6 text-center shadow-xl">
        <div
          class="w-16 h-16 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-4 border border-amber-100 shadow-inner">
          <v-icon size="34" color="amber darken-2">mdi-hammer-wrench</v-icon>
        </div>
        <h3 class="text-lg font-extrabold text-gray-900 mb-1.5">
          {{ underConstructionTitle || 'Under Construction' }}
        </h3>
        <p class="text-xs text-gray-500 max-w-xs mx-auto mb-6 leading-relaxed">
          {{ underConstructionMessage || 'This feature is currently under active development and will be available in the upcoming update.' }}
        </p>
        <v-btn depressed block :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text font-bold text-xs rounded-xl h-11"
          @click="underConstructionModal = false">
          Got it
        </v-btn>
      </v-card>
    </v-dialog>

    <!-- Employee Onboarding & Tutorial Carousel Dialog -->
    <TutorialCarouselDialog ref="appTutorialDialog" platform="app" />
  </div>
</template>

<script>
import AppTopNav from '@/app/components/layout/AppTopNav.vue'
import AppBottomNav from '@/app/components/layout/AppBottomNav.vue'
import TutorialCarouselDialog from '@/components/Workforce/Tutorial/TutorialCarouselDialog.vue'
import { initNotificationService, initLiveLocationService } from '@/app/utils/flutterBridge'
import { getUserData, getUserProfile } from '@/app/services/dashboard'

export default {
  name: 'AppLayout',
  components: {
    AppTopNav,
    AppBottomNav,
    TutorialCarouselDialog
  },
  data() {
    return {
      userData: null,
      orgLogo: '',
      orgQrCode: '',
      notificationsCount: 0,
      underConstructionModal: false,
      underConstructionTitle: 'Feature Under Construction',
      underConstructionMessage: 'This feature is currently being tuned and will be available soon.'
    }
  },
  computed: {
    navLinks() {
      return [
        {
          key: 'home',
          title: 'Home',
          to: '/app/dashboard',
          icon: 'mdi-home'
        },
        {
          key: 'attendance',
          title: 'Attendance',
          to: '/app/attendance',
          icon: 'mdi-calendar-clock'
        },
        {
          key: 'claims',
          title: 'Claims',
          to: '/app/claim',
          icon: 'mdi-cash-refund'
        },
        {
          key: 'tasks',
          title: 'Tasks',
          to: '/app/dashboard#tasks',
          icon: 'mdi-clipboard-list',
          action: 'tasks'
        },
        {
          key: 'profile',
          title: 'Profile',
          to: '/app/profile',
          icon: 'mdi-account-circle'
        }
      ]
    }
  },
  created() {
    this.userData = this.$storage?.getUniversal('user') || JSON.parse(localStorage.getItem('user')) || {}
    const s3Url = process.env.VUE_APP_S3_BASE_URL || 'https://uat-cdn.myfinfi.com/'
    const orgSettings = JSON.parse(localStorage.getItem('org-config')) || {}
    if (orgSettings?.logo) {
      this.orgLogo = `${s3Url}${orgSettings.logo}`
    }
    if (orgSettings?.orgQrCode) {
      this.orgQrCode = `${s3Url}${orgSettings.orgQrCode}`
    }
    this.fetchUserData()
  },
  mounted() {
    this.detectSystemNavInsets()
    window.addEventListener('resize', this.detectSystemNavInsets)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.detectSystemNavInsets)
    }
    const user = this.$storage?.getUniversal('user') || JSON.parse(localStorage.getItem('user')) || {}
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || ''
    initNotificationService(token, user)
    initLiveLocationService(token)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.detectSystemNavInsets)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.detectSystemNavInsets)
    }
  },
  methods: {
    detectSystemNavInsets() {
      if (typeof window === 'undefined') return
      let inset = 0

      // 1. Check native CSS env(safe-area-inset-bottom)
      const testEl = document.createElement('div')
      testEl.style.cssText = 'position:fixed;bottom:0;padding-bottom:env(safe-area-inset-bottom,0px);visibility:hidden;pointer-events:none;'
      document.body.appendChild(testEl)
      const envVal = parseFloat(window.getComputedStyle(testEl).paddingBottom) || 0
      document.body.removeChild(testEl)

      if (envVal > 0) {
        inset = envVal
      }

      // 2. Check visualViewport difference if available (and keyboard is not open)
      if (!inset && window.visualViewport && window.innerHeight) {
        const isInputFocused = document.activeElement && ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)
        if (!isInputFocused) {
          const diff = Math.round(window.innerHeight - window.visualViewport.height)
          if (diff >= 30 && diff <= 120) {
            inset = diff
          }
        }
      }

      // 3. Check screen vs available height ONLY when the WebView actually extends behind system bars
      if (!inset && /Android/i.test(navigator.userAgent) && window.screen) {
        const screenH = window.screen.height || 0
        const availH = window.screen.availHeight || 0
        const innerH = window.innerHeight || 0

        // If availHeight is smaller than screenHeight (system nav bar exists),
        // and innerHeight extends past availHeight (the WebView is drawing behind the nav bar):
        if (screenH > availH && innerH > (availH + 10)) {
          const navBarHeight = screenH - availH
          if (navBarHeight >= 30 && navBarHeight <= 100) {
            inset = navBarHeight
          }
        } else if (screenH > 0 && innerH >= (screenH - 10) && screenH > availH) {
          // Window covers the entire physical screen to the bottom edge
          const navBarHeight = screenH - availH
          if (navBarHeight >= 30 && navBarHeight <= 100) {
            inset = navBarHeight
          }
        }
      }

      if (inset > 0) {
        document.documentElement.style.setProperty('--safe-bottom-inset', `${inset}px`)
      } else {
        document.documentElement.style.removeProperty('--safe-bottom-inset')
      }
    },
    async fetchUserData() {
      try {
        const [userRes, profRes] = await Promise.allSettled([getUserData(), getUserProfile()])
        let updatedUser = {}
        if (userRes.status === 'fulfilled' && userRes.value?.data) {
          const u = userRes.value.data?.user || userRes.value.data?.data || userRes.value.data
          if (u && typeof u === 'object') {
            updatedUser = { ...updatedUser, ...u }
          }
        }
        if (profRes.status === 'fulfilled' && profRes.value?.data) {
          const p = profRes.value.data?.data || profRes.value.data?.profile || profRes.value.data
          if (p && typeof p === 'object') {
            updatedUser = { ...updatedUser, ...p }
            if (p.profile_image) {
              updatedUser.profile_image = p.profile_image
            }
          }
        }
        if (Object.keys(updatedUser).length > 0) {
          this.userData = {
            ...(this.userData || {}),
            ...updatedUser
          }
          this.$storage?.setUniversal('user', this.userData)
          localStorage.setItem('user', JSON.stringify(this.userData))
        }
      } catch (err) {
        console.warn('Could not refresh user data in AppLayout:', err)
      }
    },
    showUnderConstruction(title, message) {
      this.underConstructionTitle = title || 'Under Construction'
      this.underConstructionMessage = message || 'This feature is currently being built and will be available in the next release.'
      this.underConstructionModal = true
    },
    navToProfile() {
      this.$router.push('/app/profile')
    },
    navToNotifications() {
      this.showUnderConstruction('Alerts & Notifications', 'Notifications and alert management for the field employee app are currently under construction.')
    }
  }
}
</script>

<style scoped>
.app-header {
  height: 56px;
  position: fixed !important;
  top: 0;
  left: 0;
  right: 0;
}

.app-main-content {
  padding-top: 62px !important;
  padding-bottom: calc(4.25rem + var(--safe-bottom, 0px)) !important;
  overflow: visible !important;
}

@media (min-width: 768px) {
  .app-header {
    height: 64px;
  }
  .app-main-content {
    padding-top: 72px !important;
    padding-bottom: 2rem !important;
  }
  .app-mobile-nav {
    display: none !important;
  }
}

.app-mobile-nav {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 40 !important;
}
</style>
